import { cell, parseCsv } from './csv.js';
import { MAIN } from './types.js';
const REQUIRED = {
    participants: ['participant_id', 'display_name', 'role'],
    rooms: ['room_id', 'room_name', 'capacity'],
    rounds: ['round_id', 'sequence', 'duration_minutes'],
    assignments: ['round_id', 'participant_id', 'destination_room_id'],
};
function checkHeader(file, header, errors) {
    const missing = REQUIRED[file].filter((c) => !header.includes(c));
    if (missing.length) {
        errors.push({ file, line: 1, message: `Missing required column(s): ${missing.join(', ')}` });
        return false;
    }
    return true;
}
function required(file, line, header, cells, column, errors) {
    const v = cell(header, cells, column);
    if (v === undefined || v === '') {
        errors.push({ file, line, column, message: `Missing value for ${column}` });
        return undefined;
    }
    return v;
}
function parseIntStrict(file, line, column, raw, errors, opts = {}) {
    if (raw === undefined)
        return undefined;
    if (!/^-?\d+$/.test(raw)) {
        errors.push({ file, line, column, message: `${column} must be an integer, got "${raw}"` });
        return undefined;
    }
    const n = Number(raw);
    if (opts.min !== undefined && n < opts.min) {
        errors.push({ file, line, column, message: `${column} must be at least ${opts.min}, got ${n}` });
        return undefined;
    }
    return n;
}
const ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;
/**
 * Parse the four CSV files into a Schedule, collecting row-specific errors
 * (FR02). Reference resolution (unknown participant/room/round) is also
 * reported here so that the organiser sees it against the offending row;
 * schedule-level consistency belongs to validate.ts.
 */
export function importSchedule(files) {
    const errors = [];
    const participants = [];
    const rooms = [];
    const rounds = [];
    const assignments = [];
    // participants
    {
        const { header, rows } = parseCsv(files.participants);
        if (checkHeader('participants', header, errors)) {
            const seen = new Set();
            for (const { line, cells } of rows) {
                const id = required('participants', line, header, cells, 'participant_id', errors);
                const name = required('participants', line, header, cells, 'display_name', errors);
                const roleRaw = required('participants', line, header, cells, 'role', errors);
                let role;
                if (roleRaw !== undefined) {
                    const r = roleRaw.toLowerCase();
                    if (r === 'student' || r === 'specialist')
                        role = r;
                    else
                        errors.push({ file: 'participants', line, column: 'role', message: `role must be student or specialist, got "${roleRaw}"` });
                }
                if (id !== undefined) {
                    if (id === MAIN)
                        errors.push({ file: 'participants', line, column: 'participant_id', message: 'MAIN is reserved' });
                    if (seen.has(id))
                        errors.push({ file: 'participants', line, column: 'participant_id', message: `Duplicate participant_id "${id}"` });
                    seen.add(id);
                }
                if (id && name && role) {
                    const p = { participantId: id, displayName: name, role };
                    const email = cell(header, cells, 'email');
                    const groupId = cell(header, cells, 'group_id');
                    if (email)
                        p.email = email;
                    if (groupId)
                        p.groupId = groupId;
                    participants.push(p);
                }
            }
        }
    }
    // rooms
    {
        const { header, rows } = parseCsv(files.rooms);
        if (checkHeader('rooms', header, errors)) {
            const seen = new Set();
            for (const { line, cells } of rows) {
                const id = required('rooms', line, header, cells, 'room_id', errors);
                const name = required('rooms', line, header, cells, 'room_name', errors);
                const capacity = parseIntStrict('rooms', line, 'capacity', required('rooms', line, header, cells, 'capacity', errors), errors, { min: 1 });
                if (id !== undefined) {
                    if (id === MAIN)
                        errors.push({ file: 'rooms', line, column: 'room_id', message: 'MAIN is reserved and must not be declared as a room' });
                    if (seen.has(id))
                        errors.push({ file: 'rooms', line, column: 'room_id', message: `Duplicate room_id "${id}"` });
                    seen.add(id);
                }
                if (id && name && capacity !== undefined)
                    rooms.push({ roomId: id, roomName: name, capacity });
            }
        }
    }
    // rounds
    {
        const { header, rows } = parseCsv(files.rounds);
        if (checkHeader('rounds', header, errors)) {
            const seen = new Set();
            for (const { line, cells } of rows) {
                const id = required('rounds', line, header, cells, 'round_id', errors);
                const sequence = parseIntStrict('rounds', line, 'sequence', required('rounds', line, header, cells, 'sequence', errors), errors, { min: 1 });
                const duration = parseIntStrict('rounds', line, 'duration_minutes', required('rounds', line, header, cells, 'duration_minutes', errors), errors, { min: 1 });
                const plannedStart = cell(header, cells, 'planned_start');
                if (plannedStart && !ISO_WITH_OFFSET.test(plannedStart)) {
                    errors.push({ file: 'rounds', line, column: 'planned_start', message: `planned_start must be ISO 8601 with an offset, got "${plannedStart}"` });
                }
                if (id !== undefined) {
                    if (seen.has(id))
                        errors.push({ file: 'rounds', line, column: 'round_id', message: `Duplicate round_id "${id}"` });
                    seen.add(id);
                }
                if (id && sequence !== undefined && duration !== undefined) {
                    const r = { roundId: id, sequence, durationMinutes: duration };
                    if (plannedStart && ISO_WITH_OFFSET.test(plannedStart))
                        r.plannedStart = plannedStart;
                    rounds.push(r);
                }
            }
        }
    }
    // assignments
    {
        const { header, rows } = parseCsv(files.assignments);
        if (checkHeader('assignments', header, errors)) {
            const participantIds = new Set(participants.map((p) => p.participantId));
            const roomIds = new Set(rooms.map((r) => r.roomId));
            const roundIds = new Set(rounds.map((r) => r.roundId));
            for (const { line, cells } of rows) {
                const roundId = required('assignments', line, header, cells, 'round_id', errors);
                const participantId = required('assignments', line, header, cells, 'participant_id', errors);
                const dest = required('assignments', line, header, cells, 'destination_room_id', errors);
                if (roundId && !roundIds.has(roundId))
                    errors.push({ file: 'assignments', line, column: 'round_id', message: `Unknown round_id "${roundId}"` });
                if (participantId && !participantIds.has(participantId))
                    errors.push({ file: 'assignments', line, column: 'participant_id', message: `Unknown participant_id "${participantId}"` });
                if (dest && dest !== MAIN && !roomIds.has(dest))
                    errors.push({ file: 'assignments', line, column: 'destination_room_id', message: `Unknown destination_room_id "${dest}"` });
                const flagRaw = (cell(header, cells, 'specialist_move') ?? '').toLowerCase();
                if (roundId && participantId && dest) {
                    const a = {
                        roundId: roundId,
                        participantId: participantId,
                        destinationRoomId: dest,
                    };
                    if (flagRaw === 'true' || flagRaw === 'yes' || flagRaw === '1')
                        a.specialistMoveFlag = true;
                    assignments.push(a);
                }
            }
        }
    }
    return { schedule: { participants, rooms, rounds, assignments }, errors };
}
//# sourceMappingURL=import.js.map